/* eslint-disable @typescript-eslint/restrict-plus-operands */
import { Box, Grid, Typography } from '@mui/material';
import { Treemap, ResponsiveContainer } from 'recharts';
import { useState } from 'react';
import { ThemePalette } from 'mui.theme';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import { FlexBox } from 'components/Containers/FlexBox';
import MultiColorCircularLoader from 'views/Production/components/BoxLoader/MultiColorCircularLoader';
import { getDataKeyResponse, getDataUnit } from './utils';
import { convertToTwoDecimalPlaces } from 'components/Charts/Tooltip';
import { truncateTo2dp } from 'utils/helpers/general';
import { getColor } from 'views/Utilization/utils';
import { useAppSelector } from 'store/hook';
import AssetItem from './AssetItem';
import LoaderEvents, { LoaderEventsDuration } from './LoaderEventsDuration';
import LoaderEventsFuelConsumption from './LoaderEventsFuelConsumption';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  enableDarkTheme: boolean;
  unitSystem: string;
  customerCode: string;
  isLoading: boolean;
  isSelected: string;
  startDate: Date;
  endDate: Date;
  isPrinting: boolean;
}

const options = [
  'Production',
  'Load Per Day',
  'Hauler Load Time',
  'Production Per Hour',
  'Loader Cycle Time',
];

const sortOptions = ['Least Load Per Day', 'Most Load Per Day'];

const CustomContent = ({
  x,
  y,
  width,
  height,
  name,
  color,
  value,
  unit,
}: any) => {
  return (
    <g>
      <rect
        x={x}
        y={y}
        width={width}
        height={height}
        fill={color}
        stroke="#fff"
      />
      <text
        x={x + width / 2}
        y={y + height / 2}
        textAnchor="middle"
        fill="#fff"
        fontSize={14}
      >
        {name}
      </text>
      <text
        x={x + width / 2}
        y={y + height / 2 + 20}
        textAnchor="middle"
        fill="#fff"
        fontSize={12}
      >
        {`${convertToTwoDecimalPlaces(value)} ${unit ?? ''}`}
      </text>
    </g>
  );
};

const AssetView = ({
  enableDarkTheme,
  unitSystem,
  customerCode,
  isLoading,
  startDate,
  endDate,
  isPrinting,
}: Props) => {
  const { t } = useTranslation();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const { assets } = useAppSelector((state) => state.assetReducer);
  const [selectedSource, setSelectedSource] = useState<string>('Load Per Day');
  const [selectedSort, setSelectedSort] = useState<string>('Most Load Per Day');
  const {
    data: {
      loaders: { deviceSummary },
    },
  } = useAppSelector((state) => state.productionReducer);

  const dataKey: string = getDataKeyResponse(selectedSource);
  const dataUnit: string | undefined = getDataUnit(selectedSource, unitSystem);

  const formattedData = deviceSummary
    ?.filter((sumItem) => sumItem.device_loads_per_day !== 0)
    ?.map((item) => {
      const assetDetail = assets.find(
        (itemData) => itemData.device.deviceId === item.loader
      );
      return {
        ...item,
        // production: Number(item.device_loads_per_day),
        production: Number(item.total_load_for_selected_range),
        loadPerDay: Number(item.device_loads_per_day),
        haulerLoadTime: Number(item.device_hauler_load_time),
        productionRatePerHour: Number(item.device_loads_per_hr),
        loaderCycleTime: Number(item.device_loader_cycle_time),
        bumper_id: assetDetail?.bumperNumber,
        make: assetDetail?.make,
        model: assetDetail?.model,
      };
    });

  const sortedData = formattedData?.sort((a, b) => {
    if (selectedSort === 'Least Load Per Day') {
      return a.loadPerDay - b.loadPerDay;
    }
    return b.loadPerDay - a.loadPerDay;
  });

  let chartData = sortedData?.map((item, index, arr) => {
    const isMin =
      selectedSort === 'Least Load Per Day'
        ? index === 0
        : index === arr.length - 1;
    return {
      name: `${item.bumper_id}`,
      value:
        // @ts-expect-error: string being returned
        item[dataKey],
      color: getColor(isMin),
      unit: dataUnit,
    };
  });

  chartData = chartData?.map((item) => {
    const newValue = Number(truncateTo2dp(item?.value));

    return {
      ...item,
      value: newValue,
    };
  });

  return (
    <>
      <Box
        sx={{
          marginTop: '20px',
          color: palette.textPrimary,
        }}
      >
        <Grid container spacing={2}>
          <LoaderEventsDuration enableDarkTheme={enableDarkTheme} />
          <LoaderEventsFuelConsumption
            enableDarkTheme={enableDarkTheme}
            unitSystem={unitSystem}
          />
        </Grid>
      </Box>
      <Box
        sx={{
          marginTop: '20px',
          padding: 2,
          backgroundColor: enableDarkTheme
            ? palette.boxBackground
            : palette.toolBarBackground,
          borderRadius: '5px',
          border: `1px solid ${palette.cardBorderColor}`,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '13px',
                fontWeight: '600',
                paddingTop: '5px',
                color: palette.textSecondary,
              }}
            >
              {t('detailed_analysis')}
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
            }}
          >
            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('show_on_chart')}
            </Typography>

            <ProductionSelector
              options={options}
              selected={selectedSource}
              setSelected={setSelectedSource}
              minWidth="150px"
            />

            <Typography
              sx={{
                fontSize: '12px',
                fontWeight: '600',
                color: palette.textSecondary,
                paddingLeft: '10px',
                paddingRight: '15px',
                paddingTop: '5px',
              }}
            >
              {t('sort_by_10')}
            </Typography>

            <ProductionSelector
              options={sortOptions}
              selected={selectedSort}
              setSelected={setSelectedSort}
              minWidth="150px"
            />
          </Box>
        </Box>

        {isLoading ? (
          <MultiColorCircularLoader height="200px" />
        ) : (
          <Grid
            container
            sx={{
              marginTop: '10px',
            }}
          >
            <Grid lg={12} sm={12} md={12}>
              <Box
                sx={{
                  backgroundColor: enableDarkTheme
                    ? palette.boxBackground
                    : palette.toolBarBackground,
                  paddingLeft: '5px',
                }}
              >
                <FlexBox
                  sx={{
                    maxWidth: 'inherit',
                    height: '230px',
                    paddingTop: '20px',
                    marginRight: '10px',
                    paddingBottom: '10px',
                  }}
                >
                  {chartData?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <Treemap
                        width={800}
                        height={600}
                        data={chartData}
                        dataKey="value"
                        stroke="#fff"
                        fill={ThemePalette.typography.lightGrey}
                        content={<CustomContent />}
                      ></Treemap>
                    </ResponsiveContainer>
                  ) : (
                    <Typography
                      sx={{
                        fontSize: '12px',
                        color: palette.textPrimary,
                      }}
                    >
                      {t('no_data_available_for_the_selected_date_range')}
                    </Typography>
                  )}
                </FlexBox>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: palette.textPrimary,
                  }}
                >
                  {t('coloring_is_based_on_least_productive_asset')}
                </Typography>
              </Box>
            </Grid>
            <Grid lg={12} sm={12} md={12}>
              <Box
                sx={{
                  marginTop: '15px',
                }}
              >
                {sortedData?.length > 0 ? (
                  <>
                    {sortedData?.map((item, index) => {
                      return (
                        <div key={index}>
                          <AssetItem
                            enableDarkTheme={enableDarkTheme}
                            key={index}
                            data={item}
                            unitSystem={unitSystem}
                            customerCode={customerCode}
                            selectedSource={selectedSource}
                          />
                        </div>
                      );
                    })}
                  </>
                ) : (
                  <></>
                )}
              </Box>
            </Grid>
          </Grid>
        )}
      </Box>
    </>
  );
};

export default AssetView;
